import React, { FC, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { Select, Typography } from 'antd';
import { CbaLanguage, CbaSubject, cbaLanguages } from './cbaApi';

const { Text } = Typography;

/** What the student has chosen. An empty value means "any". */
export interface CbaFilters {
  language: CbaLanguage | '';
  subjectId: string;
  gradeId: string;
}

const labelStyle = { fontSize: 13, color: '#fff', whiteSpace: 'nowrap' as const };
const selectStyle = { width: 180 };

/**
 * The green bar on the CBA page: the curriculum, then language, subject and
 * grade. Choices are only applied when the student presses Continue, so the
 * papers below do not reload on every pick.
 *
 * Subjects depend on the language and grade, so the parent is told about every
 * pick through `onDraftChange` and hands back the subjects that fit.
 */
const CbaGreenHeader: FC<{
  filters: CbaFilters;
  subjects: CbaSubject[];
  grades: { id: string; value: string }[];
  onDraftChange: (draft: CbaFilters) => void;
  onContinue: (filters: CbaFilters) => void;
}> = ({ filters, subjects, grades, onDraftChange, onContinue }) => {
  const [draft, setDraft] = useState<CbaFilters>(filters);

  useEffect(() => setDraft(filters), [filters]);

  // A subject with no papers in the chosen language or grade is not offered,
  // so a stale pick is dropped rather than left showing.
  useEffect(() => {
    if (draft.subjectId && subjects.length > 0 && !subjects.some((subject) => subject.id === draft.subjectId)) {
      setDraft((current) => ({ ...current, subjectId: '' }));
    }
  }, [subjects, draft.subjectId]);

  const change = (next: Partial<CbaFilters>) => {
    const updated = { ...draft, ...next };
    setDraft(updated);
    onDraftChange(updated);
  };

  return (
    <Box
      sx={{
        minHeight: '48px',
        backgroundColor: '#417C4C',
        px: '6%',
        py: 1,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        columnGap: '16px',
        rowGap: '8px',
      }}>
      <Text strong style={labelStyle}>
        Curriculum
      </Text>
      <Box
        component='span'
        sx={{
          px: 1.5,
          py: '5px',
          borderRadius: '15px',
          backgroundColor: '#fff',
          color: '#1d1b20',
          fontSize: 13,
          whiteSpace: 'nowrap',
        }}>
        Local O/L - Exam Papers
      </Box>

      <Text strong style={labelStyle}>
        Language
      </Text>
      <Select
        className='green-header'
        value={draft.language || undefined}
        onChange={(language) => change({ language: language ?? '' })}
        options={cbaLanguages}
        placeholder='Select Language'
        allowClear
        style={selectStyle}
      />

      <Text strong style={labelStyle}>
        Subject
      </Text>
      <Select
        className='green-header'
        value={draft.subjectId || undefined}
        onChange={(subjectId) => change({ subjectId: subjectId ?? '' })}
        options={subjects.map((subject) => ({ value: subject.id, label: subject.title }))}
        placeholder='Select Subject'
        notFoundContent='No papers yet'
        style={selectStyle}
      />

      <Text strong style={labelStyle}>
        Grade
      </Text>
      <Select
        className='green-header'
        value={draft.gradeId || undefined}
        onChange={(gradeId) => change({ gradeId: gradeId ?? '' })}
        options={grades.map((grade) => ({ value: grade.id, label: grade.value }))}
        placeholder='Select Grade'
        allowClear
        style={selectStyle}
      />

      <Button
        onClick={() => onContinue(draft)}
        sx={{
          px: 2.5,
          height: 32,
          borderRadius: '15px',
          backgroundColor: '#fff',
          color: '#1d1b20',
          fontSize: 13,
          '&:hover': { backgroundColor: '#f0f0f0' },
        }}>
        Continue
      </Button>
    </Box>
  );
};

export default CbaGreenHeader;
